import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import './FunnelsModal.css';

const EMPTY_STATE_MESSAGE = 'Haga click en uno de los enlaces de la izquierda para mostrar el contenido de los embudos.';

const canUseValue = (value) => {
    if (typeof value === 'string') {
        return value.trim() !== '';
    }
    return value != null;
};

export default function FunnelsModal({ open, onClose, queryId }) {
    const [funnels, setFunnels] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedUrl, setSelectedUrl] = useState('');
    const [copied, setCopied] = useState(false);
    const [showIframeWarning, setShowIframeWarning] = useState(false);
    const iframeTimeoutRef = useRef(null);

    const warningMessage = useMemo(() => {
        if (!selectedUrl) return '';
        return `No se puede mostrar en esta ventana la pagina. Haga clic en la url completa "${selectedUrl}" para abrila en otra pestaña.`;
    }, [selectedUrl]);

    useEffect(() => {
        if (!open || !canUseValue(queryId)) {
            setFunnels([]);
            setSelectedUrl('');
            setError(null);
            setShowIframeWarning(false);
            return undefined;
        }

        let isMounted = true;
        const controller = new AbortController();

        const loadFunnels = async () => {
            setIsLoading(true);
            setError(null);
            setFunnels([]);
            setSelectedUrl('');
            setShowIframeWarning(false);

            try {
                const response = await fetch(`https://pulpoia-ops.com/backend/ads/getfunnels?id=${encodeURIComponent(String(queryId))}`, {
                    method: 'GET',
                    signal: controller.signal,
                });

                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}`);
                }

                const json = await response.json();
                if (!isMounted) return;
                const nextFunnels = Array.isArray(json?.funnels) ? json.funnels : [];
                setFunnels(nextFunnels);
            } catch (err) {
                if (!isMounted || controller.signal.aborted) return;
                setError(err?.message || 'No se pudieron cargar los embudos.');
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        loadFunnels();

        return () => {
            isMounted = false;
            controller.abort();
        };
    }, [open, queryId]);

    useEffect(() => {
        if (!open || !selectedUrl) {
            clearTimeout(iframeTimeoutRef.current);
            return undefined;
        }

        setShowIframeWarning(false);
        clearTimeout(iframeTimeoutRef.current);
        iframeTimeoutRef.current = setTimeout(() => {
            setShowIframeWarning(true);
        }, 4500);

        return () => {
            clearTimeout(iframeTimeoutRef.current);
        };
    }, [open, selectedUrl]);

    if (!open) {
        return null;
    }

    const handleCopyUrl = async () => {
        if (!selectedUrl) return;
        try {
            await navigator.clipboard.writeText(selectedUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch (err) {
            console.error('Error copying funnel url', err);
        }
    };

    const handleSelectUrl = (url) => {
        setSelectedUrl(url);
        setShowIframeWarning(false);
    };

    const handleIframeLoad = () => {
        clearTimeout(iframeTimeoutRef.current);
        setShowIframeWarning(false);
    };

    return (
        <div className="funnels-modal-overlay" onClick={onClose}>
            <div className="funnels-modal-container" onClick={(event) => event.stopPropagation()}>
                <div className="funnels-modal-header">
                    <div className="funnels-modal-title-group">
                        <Typography className="funnels-modal-title">Embudos disponibles en este dominio</Typography>
                        <div className="funnels-modal-url-row">
                            <Tooltip title={copied ? 'Copiado!' : 'Copiar URL'}>
                                <span>
                                    <IconButton
                                        size="small"
                                        onClick={handleCopyUrl}
                                        disabled={!selectedUrl}
                                        disableRipple
                                        disableFocusRipple
                                        disableTouchRipple
                                        className="funnels-modal-copy-btn"
                                        sx={{
                                            padding: '4px',
                                            color: '#000000',
                                            backgroundColor: 'transparent',
                                            '& svg': {
                                                color: '#000000',
                                                fill: '#000000',
                                            },
                                            '&.Mui-disabled': {
                                                color: '#000000',
                                                backgroundColor: 'transparent',
                                                opacity: 1,
                                                '& svg': {
                                                    color: '#000000',
                                                    fill: '#000000',
                                                },
                                            },
                                            '&:hover, &:active, &:focus, &.Mui-focusVisible': {
                                                backgroundColor: 'transparent',
                                                color: '#000000',
                                                '& svg': {
                                                    color: '#000000',
                                                    fill: '#000000',
                                                },
                                            },
                                        }}
                                    >
                                        <ContentCopyOutlinedIcon fontSize="small" sx={{ color: '#000000', fill: '#000000' }} />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Typography className="funnels-modal-url" title={selectedUrl || ''}>
                                {selectedUrl || 'Seleccione un enlace para ver su URL completa aqui.'}
                            </Typography>
                        </div>
                    </div>
                    <IconButton className="funnels-modal-close-btn" onClick={onClose} aria-label="Cerrar modal">
                        <span className="funnels-modal-close-label">X</span>
                    </IconButton>
                </div>

                <div className="funnels-modal-body">
                    <aside className="funnels-modal-sidebar">
                        {isLoading ? (
                            <Box className="funnels-modal-sidebar-state">
                                <CircularProgress size={28} />
                            </Box>
                        ) : error ? (
                            <Box className="funnels-modal-sidebar-state">
                                <Typography className="funnels-modal-warning">No se pudieron cargar los embudos: {error}</Typography>
                            </Box>
                        ) : funnels.length === 0 ? (
                            <Box className="funnels-modal-sidebar-state">
                                <Typography className="funnels-modal-empty">No hay embudos disponibles para este identificador.</Typography>
                            </Box>
                        ) : (
                            <ol className="funnels-modal-list">
                                {funnels.map((funnel, index) => {
                                    const url = funnel?.url || '';
                                    const isSelected = selectedUrl === url;
                                    return (
                                        <li key={`${url}-${index}`} className="funnels-modal-list-item">
                                            <a
                                                href={url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className={`funnels-modal-link${isSelected ? ' is-selected' : ''}`}
                                                onClick={(event) => {
                                                    event.preventDefault();
                                                    handleSelectUrl(url);
                                                }}
                                                title={url}
                                            >
                                                {url}
                                            </a>
                                        </li>
                                    );
                                })}
                            </ol>
                        )}
                    </aside>

                    <section className="funnels-modal-preview">
                        {!selectedUrl ? (
                            <Box className="funnels-modal-preview-state">
                                <Typography className="funnels-modal-empty">{EMPTY_STATE_MESSAGE}</Typography>
                            </Box>
                        ) : showIframeWarning ? (
                            <Box className="funnels-modal-preview-state funnels-modal-preview-warning">
                                <Typography className="funnels-modal-warning">{warningMessage}</Typography>
                                <a href={selectedUrl} target="_blank" rel="noopener noreferrer" className="funnels-modal-warning-link">
                                    {selectedUrl}
                                    <OpenInNewIcon fontSize="small" />
                                </a>
                            </Box>
                        ) : (
                            <iframe
                                title="Vista previa de embudo"
                                src={selectedUrl}
                                className="funnels-modal-iframe"
                                onLoad={handleIframeLoad}
                            />
                        )}
                    </section>
                </div>
            </div>
        </div>
    );
}